// Centralized HTTP client for the main backend server (port 3001).
// Automatically injects x-user-id, x-organisation-id, and Content-Type on every request.
// Modules only need to supply the path and business-level options — no auth plumbing needed.

type RequestOptions = Omit<RequestInit, 'headers'> & {
  /** Extra headers merged on top of the auto-injected ones. */
  headers?: Record<string, string>;
  /** Organisation ID to inject. Falls back to the x-organisation-id value already stored. */
  organisationId?: string;
};

type ApiResponse<T = unknown> = {
  data: T;
  ok: boolean;
  status: number;
};

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

// Reads the session from locally cached Supabase session (no network call).
// getSession() reads from localStorage — same JWT that Supabase already persists there.
const getSession = async () => {
  try {
    const { supabase } = await import('@foundation/utils/supabaseClient');
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session;
  } catch {
    return null;
  }
};

const buildHeaders = async (
  organisationId?: string,
  extra: Record<string, string> = {}
): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extra,
  };

  const session = await getSession();
  if (session?.access_token)
    headers['Authorization'] = `Bearer ${session.access_token}`;
  if (session?.user?.id) headers['x-user-id'] = session.user.id;
  if (organisationId) headers['x-organisation-id'] = organisationId;

  return headers;
};

// Throws a normalised Error with the server's error message when the response is not ok.
const handleResponse = async <T>(
  response: Response
): Promise<ApiResponse<T>> => {
  if (!response.ok) {
    let message = `Request failed: ${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      message = body.details ?? body.error ?? body.message ?? message;
    } catch {
      // keep the default message
    }
    throw new Error(message);
  }

  const data = (await response.json()) as T;
  return { data, ok: true, status: response.status };
};

const request = async <T>(
  method: string,
  path: string,
  { organisationId, headers: extraHeaders, body, ...rest }: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const headers = await buildHeaders(organisationId, extraHeaders);
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body,
    ...rest,
  });
  return handleResponse<T>(response);
};

export const mainClient = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>('GET', path, options),

  post: <T = unknown>(
    path: string,
    payload?: unknown,
    options?: RequestOptions
  ) =>
    request<T>('POST', path, {
      ...options,
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  put: <T = unknown>(
    path: string,
    payload?: unknown,
    options?: RequestOptions
  ) =>
    request<T>('PUT', path, {
      ...options,
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  patch: <T = unknown>(
    path: string,
    payload?: unknown,
    options?: RequestOptions
  ) =>
    request<T>('PATCH', path, {
      ...options,
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    }),

  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, options),
};
